import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { useAuth } from '@context';
import { useAuthHttp } from '@hooks';
import {
  GET_NOTIFICATION_PREFERENCES,
  UPDATE_NOTIFICATION_PREFERENCES,
} from '@constants';
import { notificationPreferencesAtom } from '../store/jotai';
import { NotificationPreferences } from './types';

interface GetResult {
  preferences: NotificationPreferences;
}

interface UpdateResult {
  preferences: NotificationPreferences;
}

export interface UpdatePreferencesPayload {
  emailDigestEnabled: boolean;
  digestFrequency: NotificationPreferences['digestFrequency'];
  categoryEnabled: Record<string, boolean>;
}

export const useNotificationPreferences = () => {
  const [preferences, setPreferences] = useAtom(notificationPreferencesAtom);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { isAuthenticated } = useAuth();
  const authHttp = useAuthHttp();
  const hasFetched = useRef(false);

  const fetchPreferences = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await authHttp.get<GetResult>(
        GET_NOTIFICATION_PREFERENCES
      );
      setPreferences(data.preferences);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to fetch notification preferences:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || hasFetched.current) return;
    hasFetched.current = true;
    fetchPreferences();
  }, [isAuthenticated, fetchPreferences]);

  const updatePreferences = useCallback(
    async (payload: UpdatePreferencesPayload) => {
      setSaving(true);
      setError(null);
      const previousPreferences = preferences;
      if (previousPreferences) {
        setPreferences({
          ...previousPreferences,
          emailDigestEnabled: payload.emailDigestEnabled,
          digestFrequency: payload.digestFrequency,
          categoryEnabled: payload.categoryEnabled,
        });
      }
      try {
        const { data } = await authHttp.post<UpdateResult>(
          UPDATE_NOTIFICATION_PREFERENCES,
          payload
        );
        setPreferences(data.preferences);
        return data.preferences;
      } catch (err) {
        setPreferences(previousPreferences);
        setError(err as Error);
        console.error('Failed to update notification preferences:', err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [authHttp, preferences, setPreferences]
  );

  return {
    preferences,
    loading,
    saving,
    error,
    fetchPreferences,
    updatePreferences,
  };
};
